import { COLORS } from '@btime/constants'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useChecklistForm, useChecklistFormActions } from '../../_form/ChecklistFormHandler'
import CheckboxInput from '../../../../../components/form/CheckboxInput'
import Message from '../../../../../components/messenger/Message'
import Separator from '../../../../../components/helpers/Separator'
import { SmallText } from '../../../../../packages/kiti/components/presentation/Text'
import { hasValue } from '../../../../../utils/helpers/value'

// UpsertChecklist (backend) rejects allowBulkExecution when any question has
// displayConditions set: bulk execution is intentionally incompatible with
// conditional logic between questions.
//
// `hasValue` treats `null` as "has a value" (Ramda's `isEmpty(null)` is `false`), but a
// question with no conditional logic is loaded from the API as `displayConditions: null`
// (GraphQL's normal shape for an unset field), not `undefined`. The backend's equivalent
// check (`present_conditions?/1` in `validations.ex`) explicitly treats `nil` as "no
// condition" — same as `[]`/`{}`. Without the same `null` exclusion here, every checklist
// whose questions simply have no conditional logic is wrongly flagged as "has conditional
// logic", permanently blocking the checkbox even though the backend would accept it.
const hasDisplayConditions = value => value != null && hasValue(value)

const hasQuestionsWithConditionalLogic = (categories = []) => {
  const stack = categories.flatMap(category => category.children || [])

  while (stack.length > 0) {
    const question = stack.pop()
    if (hasDisplayConditions(question.displayConditions)) return true
    stack.push(...(question.children || []))
  }

  return false
}

const AllowBulkExecutionSection = () => {
  const form = useChecklistForm()
  const { handleChange } = useChecklistFormActions()
  const { t } = useTranslation('editChecklist')

  const commonProps = { handleChange, form, t }
  const hasConditionalLogic = useMemo(() => hasQuestionsWithConditionalLogic(form.categories), [form.categories])
  // Only blocks turning it on; if it was already true when conditional logic was added, the
  // checkbox stays enabled so the user can still uncheck it from here.
  const isDisabled = hasConditionalLogic && !form.allowBulkExecution

  return (
    <>
      <CheckboxInput
        label={t('fieldsStep.allowBulkExecution')}
        value={form.allowBulkExecution}
        onChange={value => handleChange('allowBulkExecution', value)}
        disabled={isDisabled}
        {...commonProps}
      />
      {hasConditionalLogic ? (
        <>
          <Separator weight={2} />
          <Message
            title={t('fieldsStep.allowBulkExecutionConditionalLogicBlocked')}
            useMarkdown
            color={COLORS.dark3}
            bgColor={COLORS.lightYellow}
          />
        </>
      ) : (
        // Texto de apoio (não a mensagem de bloqueio acima, que continua no
        // Message/lightYellow de destaque): mesmo padrão de "helper text" já
        // usado abaixo de outros checkboxes do checklist (ver
        // `AutofillSection.updateBaseWarning` — Separator pequeno + SmallText
        // em cor neutra), para não competir visualmente com o nome da opção.
        <>
          <Separator weight={1} />
          <SmallText color={COLORS.dark4}>{t('fieldsStep.allowBulkExecutionConditionalLogicHint')}</SmallText>
        </>
      )}
    </>
  )
}

export default AllowBulkExecutionSection
